import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import classNames from 'classnames';
import {noop} from '@priority-software/priority-style-react';
import {formsStore} from '../../Form/stores/FormsStore';
import {getStyle} from '../../../components/Common/FieldSearch/utils/dropdownStyle';
import {
    setInputValue as formSetText,
    formSearch,
    openFormSearch,
    formSearchNext,
    zoomToFieldValue,
    zoomToNewEntry,
    zoomToAdvancedSearch,
    saveChooseState,
    setMultiSelectValues
} from '../actions/formSearch';
import {
    getFieldText,
    executeTabKey,
    getFieldOrigValue,
    getFieldName,
    getRelatedIdFieldName
} from '../../../_shared/GWTBridge/invoke/priTextBox';
import {getIsAutocomplete, returnFocus} from '../../../_shared/GWTBridge/invoke/genPage';
import {
    SearchHandlers,
    SearchState,
    useSearch
} from '../../../components/Common/FieldSearch/hooks/useSearch';
import {
    isPopup,
    removeZoomingFlag,
    setIsReactPopupOpen,
    getIsBlockedCommands
} from '../../../_shared/GWTBridge/invoke/netForm';
import {keyCodes} from '../../../constants';
import {HoveringArrowButton} from './HoveringArrowButton';
import {isRTL} from '../../../Localization';
import {HoveringInputBorder} from './HoveringInputBorder';
import {HoveringDropdown} from './HoveringDropdown';
import {searchDropdownDivId} from '../consts/ids';
import {useSelect} from '../../../components/Common/FieldSearch/hooks/useSelect';
import {InputDropdown} from './InputDropdown';
import {Column} from '../../Form/serializers/column';
import {getIsQueryForm} from '../../../_shared/GWTBridge/invoke/formTable';
import {getIsQueryMode} from '../utils/queryMode';
import {DropdownContent} from './FormDropdownContent';
import styles from '../styles/FieldSearch.module.scss';
import {keys} from '../../../constants/KeyCodes';
import {sendMonitor} from '../../../utils/monitoring';
import {isMoreInfoField, isRelatedFieldValue} from '../../../features/MoreInfo/utils/utils';
import {ZoomTypes} from '../consts/zoomTypes';
import {FormInputBtnsWrapper} from './FormInputBtnsWrapper';
import QuickCustomer from '../../../features/QuickCustomer';
import {CUSTOMER_FIELD_NAME, CUSTOMER_FORM_NAME} from '../../../features/QuickCustomer/consts';
import usePortal from 'react-useportal';
import {showErrorMessage} from '../../../_shared/Messages/actions/MessageActions';
import {getLocalizedText, texts} from '../../../constants/TextConst';
import PartFieldExpand from '../../../features/PartModal/components/PartModalExpand';
import PartsDialog from '../../../features/PartModal/components/Dialog';
import {
    PARTS_SEARCH_MONITOR,
    PARTS_SEARCH_MONITOR_EXPAND,
    PART_FIELD_NAME
} from '../../../features/PartModal/consts';

function getFormColumn(fieldNum): Column {
    const currentForm = formsStore.currentForm;
    return currentForm?.getColumnByNumber(fieldNum.toString());
}

function getIsDisableAutoComplete(
    searchState: SearchState,
    column: Column,
    canZoom: boolean
): boolean {
    return (
        searchState.isMulti ||
        searchState.isNoResults ||
        (searchState.isSearchable && !column?.isSearchAll) ||
        (!canZoom && searchState.title1 && !searchState.title2)
    );
}

export interface FormSearchProps {
    element: HTMLInputElement;
    windowRelative: boolean;
    fieldNum: number;
    searchQuery: string;
    row: number;
    inputStyle?: string;
    listStyle?: string;
    canZoom: boolean;
    zoomtype?: number;
}

export type FormSearchExposedAPI = {handleSearchChange: (value) => void; close: () => void};

export const FormSearch = forwardRef((props: FormSearchProps, ref) => {
    // refs
    const isOpening = useRef(false);
    const isAutoCompleteDisabled = useRef(false);

    // dropdown state
    const [isOpen, setIsOpen] = useState(false);
    const [isAutoComplete, setIsAutoComplete] = useState(false);
    const [isNewCustomerModalOpen, setIsNewCustomerModalOpen] = useState(false);
    const [isPartModalOpen, setIsPartModalOpen] = useState(false);

    // search actions
    const searchActions = {
        onSearchOpen: openFormSearch,
        onSearchAction: formSearch,
        onScroll: formSearchNext
    };

    // search state
    const [searchState, searchHandlers] = useSearch(searchActions) as [SearchState, SearchHandlers];

    // search handlers
    const {
        handleSearchOpen,
        handleSearchChange,
        handleScroll,
        handleSortByChange,
        handleIgnoreCaseChange,
        handleNavigationKeys,
        handleSearchByChange,
        handleSearchFromBeginningChange
    } = searchHandlers as SearchHandlers;

    // select actions
    const onSelect = value => {
        handleClose();
        const {fieldNum} = props;
        formSetText(value, {fieldNum, isAutoComplete});
    };

    const onMultiSelect = values => {
        handleClose();
        setMultiSelectValues(values);
    };

    const selectActions = {
        onSelect,
        onMultiSelect
    };

    // select state
    const {isItemSelected, selectHandlers} = useSelect(searchState, selectActions);

    // select handlers
    const {handleSelectItem, handleMultiSelect, handleSelectKeys} = selectHandlers;

    const column = getFormColumn(props.fieldNum);

    //handlers
    const handleOpen = async (isAutoComplete = false, isForceSearch = false) => {
        if (isOpening.current) return;
        isOpening.current = true;
        isAutoComplete && setIsAutoComplete(true);
        const searchQuery = getFieldText();
        try {
            await handleSearchOpen(searchQuery, isForceSearch, isAutoComplete);
        } finally {
            isOpening.current = false;
        }
    };

    const handleClose = (e?: Event) => {
        const {isSearchable, sortBy, isDesc, chooseid} = searchState;
        if (!isSearchable) {
            saveChooseState(sortBy, isDesc, chooseid);
        }
        setIsOpen(false);
        returnFocus();
    };

    const handleKeyDown = useCallback(
        event => {
            const {element} = props;
            if (
                document.activeElement === element ||
                element.contains(document.activeElement) ||
                isOpen
            )
                switch (event.keyCode) {
                    case keyCodes.F6:
                        if (event.shiftKey || searchState.isZoom) {
                            break;
                        }
                        if (!isOpen && !isOpening.current) {
                            handleOpen(false, event.ctrlKey);
                        } else {
                            isOpen && handleClose();
                            props.canZoom && handleZoom('Zoom (F6F6)');
                        }
                        break;
                    // open dropdown with SPACE only if input is empty
                    case keyCodes.SPACE:
                        const inputValue = getFieldText();
                        if (inputValue !== '' && !getIsAutocomplete()) {
                            return;
                        }
                        if (!isOpen && inputValue === '') {
                            event.stopPropagation();
                            handleOpen();
                        }
                        break;
                }
        },
        [isOpen, searchState.isZoom]
    );

    const handleInputClear = event => {
        event.stopPropagation();
        onSelect('');
    };

    const handleArrowIconClick = event => {
        event.stopPropagation();
        if (isOpen) {
            handleClose();
        } else {
            handleOpen();
        }
    };

    const handleZoom = (monitorMsg = null) => {
        if (getIsBlockedCommands()) return;

        monitorMsg && sendMonitor(monitorMsg);
        zoomToFieldValue();
    };

    const handleTabKey = event => {
        if (event.key === keys.TAB) {
            executeTabKey(event.shiftKey);
        }
    };

    // TODO: check maybe add listeners in this component.
    const handleDropdownKeyDown = event => {
        handleNavigationKeys(event);
        handleSelectKeys(event);
        handleTabKey(event);
    };

    const handlePriTextInputChange = value => {
        if (isOpen) {
            handleSearchChange(value);
        } else if (value && !isAutoCompleteDisabled.current) {
            setIsAutoComplete(true);
            handleOpen(true);
        }
    };

    const handlePriTextInputClose = () => {
        setIsAutoComplete(false);
        isOpen && handleClose();
    };

    // functions used from PriTextInput in GWT
    useImperativeHandle(
        ref,
        () => {
            return {
                handleSearchChange: handlePriTextInputChange,
                close: handlePriTextInputClose
            };
        },
        [isOpen, searchState.searchQuery]
    );

    // effects

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    });

    useEffect(() => {
        if (isOpen && isAutoComplete) {
            props.element.onblur = () => returnFocus();
            return () => (props.element.onblur = undefined);
        }
        return noop;
    }, [isOpen, isAutoComplete]);

    useEffect(() => {
        if (isOpen && isAutoComplete) {
            props.element.classList.add('ignore-react-onclickoutside');
            return () => props.element.classList.remove('ignore-react-onclickoutside');
        }
        return noop;
    }, [isOpen, isAutoComplete]);

    useEffect(() => {
        if (isOpen && !isAutoComplete) {
            setIsReactPopupOpen(true);
            return () => {
                setIsReactPopupOpen(false);
            };
        }
        return noop;
    }, [isOpen, isAutoComplete]);

    // effect when finish loading (opening)
    useEffect(() => {
        if (!searchState.isLoading) {
            if (isAutoComplete) {
                isAutoCompleteDisabled.current = getIsDisableAutoComplete(
                    searchState,
                    column,
                    props.canZoom
                );
                if (
                    isAutoCompleteDisabled.current ||
                    searchState.isZoom /* moved this condition from render method to here TODO: check that works fine, maybe add to disable condition */
                ) {
                    searchState.isZoom && removeZoomingFlag();
                } else {
                    setIsOpen(true);
                }
            } else {
                if (searchState.message) {
                    showErrorMessage({
                        content: searchState.message,
                        title: '',
                        onConfirm: returnFocus,
                        confirmButtonText: getLocalizedText(texts.confirmLabel)
                    });
                } else if (searchState.isZoom) {
                    handleZoom();
                } else if (searchState.results) {
                    setIsOpen(true);
                }
            }
        }
    }, [searchState.isLoading]);

    const handleCloseQuickCustomerModal = () => {
        setIsNewCustomerModalOpen(false);
        returnFocus();
    };

    const handleClosePartsModal = () => {
        setIsPartModalOpen(false);
        returnFocus();
    };

    const handleZoomToNewEntry = () => {
        const fieldName = getFieldName();
        const relatedName = getRelatedIdFieldName();
        const isCustomerForm = formsStore.currentForm.name === CUSTOMER_FORM_NAME;
        if (
            (CUSTOMER_FIELD_NAME === fieldName || CUSTOMER_FIELD_NAME === relatedName) &&
            !isCustomerForm
        ) {
            setIsNewCustomerModalOpen(true);
        } else {
            zoomToNewEntry();
        }
    };

    const elementRect = props.element.getBoundingClientRect();
    const dropdownStyle = getStyle(elementRect, props.windowRelative, !isAutoComplete, searchState);
    const inputDropdownStyle = props.windowRelative
        ? dropdownStyle
        : getStyle(elementRect, true, !isAutoComplete, searchState);
    const {resultsAutoMaxHeight, ...style} = inputDropdownStyle;
    let searchDropdownDiv = document.getElementById(searchDropdownDivId);
    if (!searchDropdownDiv) {
        // for tests where searchDropdownDiv does not exist
        searchDropdownDiv = document.createElement('div');
        searchDropdownDiv.setAttribute('id', searchDropdownDivId);
        document.body.appendChild(searchDropdownDiv);
    }
    const showZoomBtn = !isOpen && props.canZoom;
    const fieldName = getFieldName();
    const origValue = getFieldOrigValue();

    const showMoreInfoBtn =
        isMoreInfoField(formsStore.currentForm, column) &&
        !isOpen &&
        !getIsQueryMode() &&
        origValue?.length > 0 &&
        isRelatedFieldValue(column);

    const {Portal} = usePortal({bindTo: searchDropdownDiv});

    const shouldShowPartExpand =
        column?.name === PART_FIELD_NAME &&
        !!formsStore.currentForm.parentForm &&
        !getIsQueryMode();

    const hasTquantField = useCallback(() => {
        let hasTquant = false;
        formsStore?.currentForm?.columns?.forEach(column => {
            if (column.name === 'TQUANT' && column.readonly === false) {
                hasTquant = true;
            }
        });
        return hasTquant;
    }, []);

    return (
        <>
            {props.zoomtype !== ZoomTypes.Zoom && (
                <HoveringArrowButton
                    elementRect={elementRect}
                    windowRelative={false}
                    isOpen={isOpen}
                    onArrowClick={handleArrowIconClick}
                    onClearClick={handleInputClear}
                    isRtl={isRTL()}
                    searchQuery={searchState.searchQuery}
                />
            )}
            <FormInputBtnsWrapper
                elementRect={elementRect}
                showZoomBtn={showZoomBtn}
                showMoreInfoBtn={showMoreInfoBtn}
                handleZoom={handleZoom}
                zoomtype={props.zoomtype}
                fieldName={fieldName}
            />
            <QuickCustomer
                isOpen={isNewCustomerModalOpen}
                onClose={handleCloseQuickCustomerModal}
            />
            <PartsDialog
                formName={formsStore?.currentForm?.name}
                isOpen={isPartModalOpen}
                onClose={handleClosePartsModal}
                hasTquantField={hasTquantField}
            />
            <Portal>
                <>
                    {isOpen && isAutoComplete && !searchState.isLoading && (
                        <>
                            <HoveringInputBorder
                                elementRect={elementRect}
                                windowRelative={true}
                                isUpword={dropdownStyle.top === 'auto'}
                                isRtl={isRTL()}
                            />
                            <HoveringDropdown
                                {...searchState}
                                className={classNames(styles.searchList, props.listStyle, {
                                    [styles.popupDropdownStyle]: isPopup()
                                })}
                                dropdownStyle={style}
                                resultsAutoMaxHeight={resultsAutoMaxHeight}
                                column={column}
                                isItemSelected={isItemSelected}
                                onSelectItem={handleSelectItem}
                                onMultiSelect={handleMultiSelect}
                                onClose={handleClose}
                                onZoom={handleZoom}
                                onZoomToAdvancedSearch={zoomToAdvancedSearch}
                                onZoomToNewEntry={handleZoomToNewEntry}
                                onIgnoreCaseChange={handleIgnoreCaseChange}
                                onSearchFromBeginningChange={handleSearchFromBeginningChange}
                                onScroll={handleScroll}
                                onSearchByChange={() =>
                                    handleSearchByChange(searchState.searchQuery)
                                }
                                onSortByChange={handleSortByChange}
                                onKeyDown={handleDropdownKeyDown}
                            />
                        </>
                    )}
                    {isOpen && !isAutoComplete && !searchState.isLoading && (
                        <InputDropdown
                            {...searchState}
                            isOpen
                            triggerButton={
                                shouldShowPartExpand && (
                                    <PartFieldExpand
                                        onClick={() => {
                                            setIsPartModalOpen(true);
                                            setIsOpen(false);
                                            sendMonitor(
                                                `${PARTS_SEARCH_MONITOR}${PARTS_SEARCH_MONITOR_EXPAND}`
                                            );
                                        }}
                                    />
                                )
                            }
                            className={classNames(styles.searchList, props.listStyle, {
                                [styles.popupDropdownStyle]: isPopup()
                            })}
                            inputProps={{
                                componentClasses: classNames({
                                    [styles.readOnlyInput]: getIsQueryForm() && !getIsQueryMode(),
                                    [styles.queryModeInput]: getIsQueryMode()
                                })
                            }}
                            contentComponent={DropdownContent}
                            dropdownStyle={style}
                            resultsAutoMaxHeight={resultsAutoMaxHeight}
                            column={column}
                            isItemSelected={isItemSelected}
                            onSelectItem={handleSelectItem}
                            onMultiSelect={handleMultiSelect}
                            onClose={handleClose}
                            onZoom={handleZoom}
                            onZoomToAdvancedSearch={zoomToAdvancedSearch}
                            onZoomToNewEntry={handleZoomToNewEntry}
                            onIgnoreCaseChange={handleIgnoreCaseChange}
                            onSearchFromBeginningChange={handleSearchFromBeginningChange}
                            onScroll={handleScroll}
                            onSearchByChange={() => handleSearchByChange(searchState.searchQuery)}
                            onSortByChange={handleSortByChange}
                            onKeyDown={handleDropdownKeyDown}
                            onSearchChange={handleSearchChange}
                        />
                    )}
                </>
            </Portal>
        </>
    );
});
